import React, { useEffect, useMemo, useState } from 'react';
import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import { SmartSelect, SearchAhead, DateField } from '../dropdown';
import type { SmartSelectOption } from '../dropdown';
import type {
  RecurringTodo,
  CreateRecurringTodoRequest,
  UpdateRecurringTodoRequest,
  TodoPriority,
  RecurringTodoFrequency,
  RecurringTodoOutputKind,
  RecurringTicketStatus,
  CustomerIndexItemDto,
  WorkItem,
  TemplateTaskConfig,
} from '@techgitter/contracts';
import type { TicketTypeTemplate } from '../../types/ticketTypeTemplate';
import { useUsers } from '../../hooks/useUsers';
import { getCustomerIndex } from '../../services/customerIndexApi';
import { searchWorkItems } from '../../services/workItemDataService';
import { useTicketTypeTemplates } from '../../hooks/useTicketTypeTemplates';
import { useProductCatalog } from '../../hooks/useProductCatalog';
import { usePricingProfilesConfig } from '../../hooks/usePricingProfilesConfig';
import { useTaskLists } from '../../hooks/useTaskLists';
import { useTaskDropdowns } from '../../hooks/useTaskDropdowns';
import { getTemplateTaskConfigs } from '../../services/templateTaskConfigService';
import { GroupedTicketTypePicker } from '../tickets/GroupedTicketTypePicker';
import { buildJobPayloadFromTemplate } from '../../utils/buildJobPayloadFromTemplate';
import { standardBusinessHoursReference } from '../../utils/timeWindowResolver';
import { buildUserColorIndex, resolveUserColor } from '../../utils/userColorIndex';
import { PRIORITY_OPTIONS, FREQUENCY_OPTIONS, toDateInputValue } from '../../utils/todoDisplay';
import './RecurringTodoPanel.css';

/** Output-kind toggle options. */
const OUTPUT_KIND_OPTIONS: SmartSelectOption[] = [
  { value: 'todo', label: 'To-Do task' },
  { value: 'ticket', label: 'Ticket' },
];

/** Status a recurring ticket is created in. Constrained to the two states the
 *  job-create pipeline supports on creation. */
const TICKET_STATUS_OPTIONS: SmartSelectOption[] = [
  { value: 'new', label: 'New' },
  { value: 'resolved', label: 'Resolved' },
];

/** A ticket-type template is eligible for recurring generation only if it has
 *  no strictly-required additional fields — there is no human at generation
 *  time to supply them (license-picker fields are strict, so per-license
 *  templates are excluded here too). */
const templateHasRequiredFields = (t: TicketTypeTemplate): boolean =>
  (t.additionalFields ?? []).some((f) => f.required === 'strict');

/** Initials (max 2) from a display name, for the Assign-To avatar. */
const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || '?';

interface Option {
  id: string;
  label: string;
}

/** SearchAhead result for the Work Item field — carries the work-item id so a
 *  pick can be resolved back to {id, label}. */
interface WorkItemResult {
  code: string;
  description: string;
  id: string;
}

interface RecurringTodoPanelProps {
  /** The recurring task being edited, or null when creating. */
  recurring: RecurringTodo | null;
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (payload: CreateRecurringTodoRequest | UpdateRecurringTodoRequest) => Promise<void>;
}

/**
 * Right-aligned panel for creating / editing a recurring task. Reuses the shared
 * `SidePanel` shell + `FormField` controls (the former `RecurringTodoDialog`
 * modal has been retired), so it reads identically to the sibling To-Do task
 * panel and the calendar panels. Lives in the right column of
 * `RecurringTasksAdminPage`'s `.recurring-layout` grid. See sm4rt-web-ui §6/§7.
 */
export const RecurringTodoPanel: React.FC<RecurringTodoPanelProps> = ({
  recurring,
  isSaving,
  error,
  onClose,
  onSave,
}) => {
  const isEdit = recurring !== null;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TodoPriority>('medium');
  const [frequency, setFrequency] = useState<RecurringTodoFrequency>('Monthly');
  const [nextRun, setNextRun] = useState('');
  const [endDate, setEndDate] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [assigneeId, setAssigneeId] = useState('');
  const [customer, setCustomer] = useState<Option | null>(null);
  const [workItem, setWorkItem] = useState<Option | null>(null);

  // Ticket-kind state
  const [outputKind, setOutputKind] = useState<RecurringTodoOutputKind>('todo');
  const [ticketTypeId, setTicketTypeId] = useState<string | null>(null);
  const [ticketStatus, setTicketStatus] = useState<RecurringTicketStatus>('new');
  // When editing, the saved line-item snapshot is preserved on save. Setting
  // this opts into regenerating line items fresh from the current template
  // (recovery path for a snapshot that has drifted).
  const [refreshLineItems, setRefreshLineItems] = useState(false);
  const isTicket = outputKind === 'ticket';

  const { users, isLoading: usersLoading } = useUsers();

  // Customer options (loaded once when the panel mounts). Keep the full index
  // records too — the ticket-kind snapshot needs the RepairShopr id, customer
  // type, designation and city to price template line items correctly.
  const [customerOptions, setCustomerOptions] = useState<Option[]>([]);
  const [customerItemsById, setCustomerItemsById] = useState<Map<string, CustomerIndexItemDto>>(new Map());
  useEffect(() => {
    const controller = new AbortController();
    getCustomerIndex(controller.signal)
      .then((res) => {
        setCustomerOptions(
          res.items.map((c: CustomerIndexItemDto) => ({ id: c.dataverseCustomerId, label: c.displayName })),
        );
        setCustomerItemsById(new Map(res.items.map((c: CustomerIndexItemDto) => [c.dataverseCustomerId, c])));
      })
      .catch(() => {/* lookup is optional — ignore load errors */});
    return () => controller.abort();
  }, []);

  // Ticket-kind catalogs. Loaded lazily via hooks; templates are fetched for
  // the selected customer so per-customer line-item overrides are applied to
  // the snapshot. Products + pricing config drive line-item pricing.
  const { templates, isLoading: templatesLoading } = useTicketTypeTemplates(isTicket ? customer?.id : null);
  const { products, isLoading: productsLoading } = useProductCatalog();
  const { config: pricingProfilesConfig, isLoading: pricingLoading } = usePricingProfilesConfig();

  // Only templates safe to generate headlessly (no strictly-required fields).
  const eligibleTemplates = useMemo(
    () => templates.filter((t) => !templateHasRequiredFields(t)),
    [templates],
  );
  const selectedTemplate = useMemo<TicketTypeTemplate | null>(
    () => (ticketTypeId ? eligibleTemplates.find((t) => t.ticketTypeId === ticketTypeId) ?? null : null),
    [eligibleTemplates, ticketTypeId],
  );

  // Task-list catalogs for ticket kind: the checklist bound to the selected
  // template (via cr001_template_task_configses) is materialised into the
  // snapshot so the generated ticket carries it. Configs are fetched for the
  // selected customer so per-customer task-config overrides apply.
  const { taskListCatalog } = useTaskLists();
  const { dropdownCatalog } = useTaskDropdowns();
  const [templateTaskConfigs, setTemplateTaskConfigs] = useState<TemplateTaskConfig[]>([]);
  useEffect(() => {
    if (!isTicket) return;
    const controller = new AbortController();
    getTemplateTaskConfigs({ customerId: customer?.id, signal: controller.signal })
      .then((catalog) => setTemplateTaskConfigs(catalog.configs))
      .catch(() => {/* checklist attach is optional — ignore load errors */});
    return () => controller.abort();
  }, [isTicket, customer?.id]);

  const selectedTaskConfig = useMemo<TemplateTaskConfig | null>(
    () =>
      selectedTemplate
        ? templateTaskConfigs.find((c) => c.ticketTypeId === selectedTemplate.ticketTypeId) ?? null
        : null,
    [templateTaskConfigs, selectedTemplate],
  );

  // Work item options — searched on demand
  const [workItemInput, setWorkItemInput] = useState('');
  const [workItemOptions, setWorkItemOptions] = useState<Option[]>([]);
  useEffect(() => {
    const term = workItemInput.trim();
    if (term.length < 2) {
      setWorkItemOptions([]);
      return;
    }
    const controller = new AbortController();
    const handle = setTimeout(() => {
      searchWorkItems(term, controller.signal)
        .then((items: WorkItem[]) => {
          setWorkItemOptions(
            items.map((w) => ({ id: w.guid, label: `${w.number ? `#${w.number} ` : ''}${w.title}`.trim() })),
          );
        })
        .catch(() => {/* optional — ignore */});
    }, 300);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [workItemInput]);

  // Seed form state when the panel opens / target changes
  useEffect(() => {
    if (recurring) {
      setTitle(recurring.title ?? '');
      setDescription(recurring.description ?? '');
      setPriority(recurring.priority ?? 'medium');
      setFrequency(recurring.frequency ?? 'Monthly');
      setNextRun(toDateInputValue(recurring.nextRun));
      setEndDate(toDateInputValue(recurring.endDate));
      setEnabled(recurring.enabled ?? true);
      setAssigneeId(recurring.assignedToId ?? '');
      setCustomer(recurring.customer ? { id: recurring.customer.id, label: recurring.customer.name ?? recurring.customer.id } : null);
      const wiLabel = recurring.workItem
        ? `${recurring.workItem.number ? `#${recurring.workItem.number} ` : ''}${recurring.workItem.title ?? recurring.workItem.id}`.trim()
        : '';
      setWorkItem(recurring.workItem ? { id: recurring.workItem.id, label: wiLabel } : null);
      setWorkItemInput(wiLabel);
      setOutputKind(recurring.outputKind ?? 'todo');
      setTicketTypeId(recurring.ticketTypeId ?? null);
      setTicketStatus(recurring.ticketStatus ?? 'new');
      setRefreshLineItems(false);
    } else {
      setTitle('');
      setDescription('');
      setPriority('medium');
      setFrequency('Monthly');
      setNextRun('');
      setEndDate('');
      setEnabled(true);
      setAssigneeId('');
      setCustomer(null);
      setWorkItem(null);
      setWorkItemInput('');
      setOutputKind('todo');
      setTicketTypeId(null);
      setTicketStatus('new');
      setRefreshLineItems(false);
    }
  }, [recurring]);

  // Escape closes the panel. (When a dropdown is open it consumes Escape and
  // stops propagation, so only the dropdown closes.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const trimmedTitle = title.trim();
  const endBeforeNext = !!endDate && endDate < nextRun;
  // Ticket-kind needs a customer + template + loaded catalogs to snapshot a
  // correctly-priced payload; the title comes from the template so it isn't
  // required. To-do kind keeps the original title requirement.
  const ticketReady =
    !!customer && !!selectedTemplate && !productsLoading && !templatesLoading && !pricingLoading;
  const scheduleValid = nextRun.length > 0 && !endBeforeNext;
  const isValid = isTicket
    ? ticketReady && scheduleValid
    : trimmedTitle.length > 0 && scheduleValid;

  // A saved snapshot can be preserved on edit only while the customer is
  // unchanged (a customer change must re-price line items). This also gates the
  // "refresh from template" control — there is nothing to preserve otherwise.
  const canPreserveLineItems =
    isTicket &&
    isEdit &&
    recurring?.customer?.id === customer?.id &&
    (recurring?.ticketPayload?.lineItems?.length ?? 0) > 0;

  // Assign-To options + avatar render-item (colour resolved by user id so it
  // matches the avatar shown for that user everywhere else in the app).
  const assigneeOptions = useMemo<SmartSelectOption[]>(
    () => users.map((u) => ({ value: u.guid, label: u.fullname || u.email })),
    [users],
  );
  const colorIndex = useMemo(() => buildUserColorIndex(users), [users]);
  const renderAssignee = useMemo(
    () => (option: SmartSelectOption) => {
      const colour = resolveUserColor(colorIndex, { id: option.value, name: option.label });
      return (
        <>
          <span
            className="sm4rt-select__avatar"
            style={{ background: colour.bg, color: colour.fg }}
          >
            {initialsOf(option.label)}
          </span>
          <span className="sm4rt-dd__label">{option.label}</span>
        </>
      );
    },
    [colorIndex],
  );

  // Customer SmartSelect options — keep the seeded customer present even if it
  // isn't in the loaded index, so its name still renders in the closed trigger.
  const customerSelectOptions = useMemo<SmartSelectOption[]>(() => {
    const opts = customerOptions.map((c) => ({ value: c.id, label: c.label }));
    if (customer && !opts.some((o) => o.value === customer.id)) {
      return [{ value: customer.id, label: customer.label }, ...opts];
    }
    return opts;
  }, [customerOptions, customer]);

  const workItemResults = useMemo<WorkItemResult[]>(
    () => workItemOptions.map((o) => ({ code: o.label, description: '', id: o.id })),
    [workItemOptions],
  );

  const handleSave = async () => {
    if (!isValid) return;

    if (isTicket) {
      const customerItem = customer ? customerItemsById.get(customer.id) : undefined;
      if (!customer || !selectedTemplate || !customerItem) return;

      // When re-saving an existing definition whose customer is unchanged,
      // preserve the previously-snapshotted line items rather than
      // re-materialising them from the template — an edit that only touches the
      // checklist/schedule must not silently regenerate (and drift) line items
      // off a template that may have changed since. A customer change, a new
      // definition, or an explicit "refresh from template" all materialise fresh.
      const preservedLineItems =
        canPreserveLineItems && !refreshLineItems
          ? recurring!.ticketPayload!.lineItems
          : undefined;

      // Snapshot the fully-resolved job payload now, using the current
      // template + customer + prices. The generator replays this verbatim.
      const ticketPayload = buildJobPayloadFromTemplate({
        template: selectedTemplate,
        customer: {
          dataverseId: customerItem.dataverseCustomerId,
          repairShoprId: customerItem.repairShoprCustomerId,
          customerType: customerItem.customerType,
          designation: customerItem.designation,
        },
        status: ticketStatus,
        products,
        pricingProfilesConfig,
        ticketDate: nextRun,
        taskConfig: selectedTaskConfig,
        taskListCatalog: taskListCatalog.taskLists,
        taskDropdowns: dropdownCatalog.dropdowns,
        lineItemsOverride: preservedLineItems,
        // A recurring snapshot is replayed on every run, so price time-sensitive
        // line items (e.g. support labour) against a standard business-hours
        // reference rather than the save-time window — otherwise saving in the
        // evening / on a weekend freezes an after-hours SKU + rate.
        pricingNow: standardBusinessHoursReference(
          nextRun ? new Date(`${nextRun}T09:00:00`) : new Date(),
        ),
      });

      const payload: CreateRecurringTodoRequest | UpdateRecurringTodoRequest = {
        // Title drives the recurring-list display; derive it from the ticket.
        title: ticketPayload.subject || selectedTemplate.displayName,
        priority,
        frequency,
        nextRun,
        endDate: endDate || undefined,
        enabled,
        customerId: customer.id,
        outputKind: 'ticket',
        ticketTypeId: selectedTemplate.ticketTypeId,
        ticketStatus,
        ticketPayload,
      };
      await onSave(payload);
      return;
    }

    const payload: CreateRecurringTodoRequest | UpdateRecurringTodoRequest = {
      title: trimmedTitle,
      description: description.trim() || undefined,
      priority,
      frequency,
      nextRun,
      endDate: endDate || undefined,
      enabled,
      assignedToId: assigneeId || undefined,
      customerId: customer?.id,
      workItemId: workItem?.id,
      outputKind: 'todo',
    };
    await onSave(payload);
  };

  return (
    <SidePanel
      title={isEdit ? 'Edit recurring task' : 'New recurring task'}
      submitLabel={isSaving ? 'Saving…' : isEdit ? 'Save changes' : 'Add task'}
      isValid={isValid && !isSaving}
      onClose={onClose}
      onSubmit={handleSave}
    >
      {error && <div className="side-panel__alert side-panel__alert--error">{error}</div>}

      {/* What each run generates */}
      <SmartSelect
        label="Type"
        value={outputKind}
        options={OUTPUT_KIND_OPTIONS}
        onChange={(v) => v && setOutputKind(v as RecurringTodoOutputKind)}
      />

      {isTicket ? (
        <>
          <GroupedTicketTypePicker
            value={selectedTemplate}
            templates={eligibleTemplates}
            isLoading={templatesLoading}
            onChange={(t) => setTicketTypeId(t?.ticketTypeId ?? null)}
            label="Ticket type *"
            placeholder={customer ? 'Search or select ticket type' : 'Select a customer first'}
            disabled={!customer}
          />
          <p className="recurring-hint">
            Line items and prices are captured from the template and replayed on each run.
          </p>
          {canPreserveLineItems &&
            (refreshLineItems ? (
              <p className="recurring-hint recurring-hint--warning">
                Line items will be regenerated from the current template on save.{' '}
                <button
                  type="button"
                  className="recurring-hint__action"
                  onClick={() => setRefreshLineItems(false)}
                >
                  Keep saved line items
                </button>
              </p>
            ) : (
              <p className="recurring-hint">
                Saved line items are preserved when you save.{' '}
                <button
                  type="button"
                  className="recurring-hint__action"
                  onClick={() => setRefreshLineItems(true)}
                >
                  Refresh line items from template
                </button>
              </p>
            ))}
          {selectedTaskConfig && (
            <p className="recurring-hint">
              A checklist is bound to this ticket type and will be attached to each generated ticket.
            </p>
          )}
        </>
      ) : (
        <>
          <FormField label="Title *">
            <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} />
          </FormField>

          <FormField label="Description">
            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </FormField>
        </>
      )}

      {/* Priority (to-do only) + Frequency */}
      <div className="side-panel__field-row">
        {!isTicket && (
          <SmartSelect
            label="Priority"
            value={priority}
            options={PRIORITY_OPTIONS}
            onChange={(v) => v && setPriority(v as TodoPriority)}
          />
        )}
        <SmartSelect
          label="Frequency"
          value={frequency}
          options={FREQUENCY_OPTIONS}
          onChange={(v) => v && setFrequency(v as RecurringTodoFrequency)}
        />
      </div>

      {/* Next run + End date */}
      <div className="side-panel__field-row">
        <DateField
          label="Next run *"
          value={nextRun || null}
          onChange={(iso) => setNextRun(iso ?? '')}
        />
        <DateField
          label="End date"
          value={endDate || null}
          onChange={(iso) => setEndDate(iso ?? '')}
        />
      </div>
      {endBeforeNext && (
        <p className="recurring-hint recurring-hint--warning">End date must be on or after the next run.</p>
      )}

      {/* Assign To (to-do only) */}
      {!isTicket && (
        <SmartSelect
          label="Assign To"
          value={assigneeId || null}
          options={assigneeOptions}
          onChange={(val) => setAssigneeId(val ?? '')}
          renderItem={renderAssignee}
          placeholder={usersLoading ? 'Loading…' : 'Select…'}
          disabled={usersLoading}
        />
      )}

      {/* Customer — optional for to-dos, required for tickets */}
      <SmartSelect
        label={isTicket ? 'Customer *' : 'Customer (optional)'}
        value={customer?.id ?? null}
        options={customerSelectOptions}
        onChange={(val) => {
          if (!val) {
            setCustomer(null);
            return;
          }
          const found = customerOptions.find((c) => c.id === val);
          setCustomer(found ?? customer ?? { id: val, label: val });
        }}
        placeholder="Select a customer…"
      />

      {/* Ticket status (ticket only) */}
      {isTicket && (
        <SmartSelect
          label="Create ticket as"
          value={ticketStatus}
          options={TICKET_STATUS_OPTIONS}
          onChange={(v) => v && setTicketStatus(v as RecurringTicketStatus)}
        />
      )}

      {/* Work item (to-do only) */}
      {!isTicket && (
        <SearchAhead
          label="Work item (optional)"
          placeholder="Search work items…"
          value={workItemInput}
          results={workItemResults}
          onSearch={(text) => {
            setWorkItemInput(text);
            if (!text) setWorkItem(null);
          }}
          onSelect={(r) => {
            setWorkItem({ id: r.id, label: r.code });
            setWorkItemInput(r.code);
          }}
        />
      )}

      {/* Enabled */}
      <label className="side-panel__checkbox">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        Enabled
      </label>
    </SidePanel>
  );
};
